import { ContactKind, ManagerModel } from '@ail/customers-api'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useManagers } from '../../../../hooks/useManagers'
import { useSettings } from '../../../../hooks/useSettings'
import { saveManager } from '../../../../redux/actions/appDataActions'
import { useAppDispatch } from '../../../../redux/store'

export function ManagerSelector() {
  const [managerSearchString, setManagerSearchString] = useState('')
  const [managersVisibility, setManagersVisibility] = useState(false)
  const [selectedManager, saveSelectedManager] = useSettings('selectedManager')
  const managersRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('common')
  const managers = useManagers()
  const dispatch = useAppDispatch()

  useEffect(() => {
    let selectedManagerName = ''
    let selectedManagerEmail = ''
    if (selectedManager) {
      const parts = selectedManager.split(' | ')
      const managerEmail = parts.at(0)?.toLowerCase()
      const managerName = parts.at(1)?.toLowerCase()
      const manager = managers.find(
        (m) =>
          m.displayName?.toLowerCase() === managerName &&
          m.contacts.some((c) => c.kind === ContactKind.Email && c.value?.toLowerCase() === managerEmail)
      )
      selectedManagerName = manager?.displayName ?? ''
      selectedManagerEmail = managerEmail ?? ''
    }

    dispatch(saveManager(selectedManagerEmail))
    setManagerSearchString(selectedManagerName)
  }, [dispatch, managers, selectedManager])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!managersRef?.current?.contains(event.target as Node)) {
        setManagersVisibility(false)
      }
    }

    if (managersVisibility) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [managersVisibility])

  const visibleManagers = useMemo(() => {
    const active: ManagerModel[] = []
    const inactive: ManagerModel[] = []
    for (const m of managers) {
      if (
        managerSearchString &&
        !m.displayName?.toLowerCase().includes(managerSearchString) &&
        !m.contacts.some((c) => c.kind === ContactKind.Email && c.value?.includes(managerSearchString))
      ) {
        continue
      }

      if (m.active || m.firstName?.toLowerCase().includes('team')) {
        active.push(m)
      } else {
        inactive.push(m)
      }
    }
    return { active, inactive }
  }, [managerSearchString, managers])

  const inputName = useMemo(() => Math.random().toString(36).slice(2, 18), [])

  const selectManager = (manager?: ManagerModel) => {
    const managerEmail = manager?.contacts.find((c) => c.kind === ContactKind.Email)?.value ?? ''
    const managerName = manager?.displayName ?? ''
    if (managerEmail && managerName) {
      saveSelectedManager(`${managerEmail} | ${managerName}`)
    } else {
      saveSelectedManager('')
    }
    setManagersVisibility(false)
  }

  return (
    <div className="input-area small" ref={managersRef}>
      <input
        name={inputName}
        autoComplete="none"
        list="autocompleteOff"
        value={managerSearchString}
        placeholder={t('ALL')}
        onInput={(e) => setManagerSearchString(e.currentTarget.value)}
        style={{ maxWidth: '150px' }}
        className="transparent"
        onClick={() => setManagersVisibility(true)}
      />
      <i
        onClick={() => setManagersVisibility(!managersVisibility)}
        style={{ position: 'absolute', right: '5px', top: '4px' }}
        className={`icon-${managersVisibility ? 'up' : 'bottom'}`}
      />
      {managersVisibility && (
        <div
          style={{
            position: 'absolute',
            width: 200,
            maxHeight: 350,
            overflowY: 'auto',
          }}
        >
          <div className="contract-item" onClick={() => selectManager(undefined)}>
            {t('ALL')}
          </div>
          {!!visibleManagers.active.length && (
            <>
              <div className="contract-item unselectable"> ACTIVE </div>
              {visibleManagers.active.map((el) => {
                return (
                  <div className="contract-item" key={el.displayName} onClick={() => selectManager(el)}>
                    {el.displayName}
                  </div>
                )
              })}
            </>
          )}
          {!!visibleManagers.inactive.length && (
            <>
              <div className="contract-item unselectable"> INACTIVE </div>
              {visibleManagers.inactive.map((el) => {
                return (
                  <div className="contract-item" key={el.displayName} onClick={() => selectManager(el)}>
                    {el.displayName}
                  </div>
                )
              })}
            </>
          )}
        </div>
      )}
    </div>
  )
}
